import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { getPublicInstructors, getPublicOrganizations } from '../utils/publicProfiles';
import {
  getDiscoverableServiceFacets,
  searchPublicServices,
} from '../utils/services';

function DiscoveryPage() {
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [services, setServices] = useState([]);
  const [filters, setFilters] = useState({
    searchTerm: '',
    discipline: '',
    language: '',
    serviceType: '',
    locationType: '',
    packageEligibleOnly: false,
  });

  useEffect(() => {
    const loadDiscovery = async () => {
      setLoading(true);
      try {
        const [nextOrganizations, nextInstructors, nextServices] = await Promise.all([
          getPublicOrganizations(db),
          getPublicInstructors(db),
          searchPublicServices(db),
        ]);
        setOrganizations(nextOrganizations);
        setInstructors(nextInstructors);
        setServices(nextServices);
      } finally {
        setLoading(false);
      }
    };

    loadDiscovery();
  }, []);

  const visibleServices = useMemo(
    () =>
      services.filter((service) => {
        if (filters.discipline && service.discipline !== filters.discipline) return false;
        if (filters.serviceType && service.serviceType !== filters.serviceType) return false;
        if (filters.locationType && service.locationType !== filters.locationType) return false;
        if (filters.packageEligibleOnly && !service.packageEligible) return false;
        if (filters.searchTerm) {
          const haystack = [
            service.name,
            service.shortDescription,
            service.discipline,
            service.serviceType,
            service.locationLabel,
          ].join(' ').toLowerCase();
          return haystack.includes(filters.searchTerm.toLowerCase());
        }
        return true;
      }),
    [filters, services]
  );

  const serviceFacets = useMemo(
    () => getDiscoverableServiceFacets(services) || {
      disciplines: [],
      serviceTypes: [],
      locationTypes: [],
      durations: [],
    },
    [services]
  );

  const visibleOrganizations = useMemo(
    () =>
      organizations.filter((profile) => {
        if (filters.discipline && !profile.disciplines.includes(filters.discipline)) return false;
        if (filters.language && !profile.languages.includes(filters.language)) return false;
        if (filters.searchTerm) {
          const haystack = [
            profile.publicName,
            profile.publicShortDescription,
            profile.locationLabel,
          ].join(' ').toLowerCase();
          return haystack.includes(filters.searchTerm.toLowerCase());
        }
        return true;
      }),
    [filters, organizations]
  );

  const visibleInstructors = useMemo(
    () =>
      instructors.filter((profile) => {
        if (filters.discipline && !profile.disciplines.includes(filters.discipline)) return false;
        if (filters.language && !profile.languages.includes(filters.language)) return false;
        if (filters.searchTerm) {
          const haystack = [
            profile.displayName,
            profile.shortBio,
            ...(profile.specialties || []),
          ].join(' ').toLowerCase();
          return haystack.includes(filters.searchTerm.toLowerCase());
        }
        return true;
      }),
    [filters, instructors]
  );

  return (
    <div className="mx-auto max-w-6xl p-6">
      <section className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Discovery</p>
        <h1 className="mt-3 text-3xl font-semibold text-slate-950">Find organizations and instructors</h1>
        <p className="mt-3 max-w-3xl text-sm text-slate-600">
          Browse published organizations and instructor profiles across disciplines, languages, and coaching styles.
        </p>

        <div className="mt-6 grid gap-3 md:grid-cols-3">
          <input
            value={filters.searchTerm}
            onChange={(event) => setFilters((current) => ({ ...current, searchTerm: event.target.value }))}
            placeholder="Search by name or keyword"
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
          />
          <input
            value={filters.discipline}
            onChange={(event) => setFilters((current) => ({ ...current, discipline: event.target.value }))}
            placeholder="Filter by discipline"
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
          />
          <input
            value={filters.language}
            onChange={(event) => setFilters((current) => ({ ...current, language: event.target.value }))}
            placeholder="Filter by language"
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
          />
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          <select
            value={filters.serviceType}
            onChange={(event) => setFilters((current) => ({ ...current, serviceType: event.target.value }))}
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
          >
            <option value="">All service types</option>
            {serviceFacets.serviceTypes.map((serviceType) => (
              <option key={serviceType} value={serviceType}>{serviceType}</option>
            ))}
          </select>
          <select
            value={filters.locationType}
            onChange={(event) => setFilters((current) => ({ ...current, locationType: event.target.value }))}
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
          >
            <option value="">All location types</option>
            {serviceFacets.locationTypes.map((locationType) => (
              <option key={locationType} value={locationType}>{locationType}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={filters.packageEligibleOnly}
              onChange={(event) => setFilters((current) => ({ ...current, packageEligibleOnly: event.target.checked }))}
            />
            Package-eligible only
          </label>
        </div>
      </section>

      <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-slate-950">Services</h2>
          <span className="text-sm text-slate-500">{visibleServices.length} visible</span>
        </div>
        {loading ? (
          <p className="mt-4 text-sm text-slate-600">Loading services...</p>
        ) : visibleServices.length === 0 ? (
          <p className="mt-4 text-sm text-slate-600">No public services match the current filters.</p>
        ) : (
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {visibleServices.map((service) => (
              <div
                key={service.serviceId}
                className="rounded-2xl border border-slate-200 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-slate-900">{service.name}</h3>
                    {service.shortDescription ? (
                      <p className="mt-1 text-sm text-slate-600">{service.shortDescription}</p>
                    ) : null}
                  </div>
                  {service.packageEligible ? (
                    <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">Package eligible</span>
                  ) : null}
                </div>
                <p className="mt-3 text-sm text-slate-600">
                  {service.price} {service.currency} · {service.durationMinutes} min
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {service.discipline || 'General'} · {service.serviceType || 'Session'}
                  {service.locationType ? ` · ${service.locationType}` : ''}
                </p>
                {service.packageEligible ? (
                  <p className="mt-2 text-xs text-slate-500">
                    Compare packages on the provider profile before booking this service.
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-8 grid gap-8 lg:grid-cols-2">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold text-slate-950">Organizations</h2>
            <span className="text-sm text-slate-500">{visibleOrganizations.length} visible</span>
          </div>
          {loading ? (
            <p className="mt-4 text-sm text-slate-600">Loading organizations...</p>
          ) : visibleOrganizations.length === 0 ? (
            <p className="mt-4 text-sm text-slate-600">No public organizations match the current filters.</p>
          ) : (
            <div className="mt-4 space-y-4">
              {visibleOrganizations.map((profile) => (
                <Link
                  key={profile.organizationId}
                  to={`/orgs/${profile.publicSlug}`}
                  className="block rounded-2xl border border-slate-200 p-4 hover:border-slate-300"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-slate-900">{profile.publicName}</h3>
                      {profile.publicShortDescription ? (
                        <p className="mt-1 text-sm text-slate-600">{profile.publicShortDescription}</p>
                      ) : null}
                    </div>
                    {profile.featured ? (
                      <span className="rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">Featured</span>
                    ) : null}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold text-slate-950">Instructors</h2>
            <span className="text-sm text-slate-500">{visibleInstructors.length} visible</span>
          </div>
          {loading ? (
            <p className="mt-4 text-sm text-slate-600">Loading instructors...</p>
          ) : visibleInstructors.length === 0 ? (
            <p className="mt-4 text-sm text-slate-600">No public instructors match the current filters.</p>
          ) : (
            <div className="mt-4 space-y-4">
              {visibleInstructors.map((profile) => (
                <Link
                  key={profile.userId}
                  to={`/instructors/${profile.publicSlug}`}
                  className="block rounded-2xl border border-slate-200 p-4 hover:border-slate-300"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-slate-900">{profile.displayName}</h3>
                      {profile.shortBio ? (
                        <p className="mt-1 text-sm text-slate-600">{profile.shortBio}</p>
                      ) : null}
                    </div>
                    {profile.featured ? (
                      <span className="rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">Featured</span>
                    ) : null}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default DiscoveryPage;
